import { api, ApiClient, asList } from './client';
import type {
  AnalyticsMetrics,
  ApiKey,
  AssessResult,
  AuditEvent,
  AuthResult,
  CreatedSession,
  Dashboard,
  Role,
  Scenario,
  ScenarioContent,
  ScenarioTemplate,
  ScenarioVersion,
  Session,
  TtsRequest,
  TtsResult,
  TtsVoice,
  User,
  UserPreferences,
  UserProgress,
  VoiceProfile,
  VoiceProfileInput,
} from './types';

export type SsoProvider = 'google' | 'microsoft' | 'okta';
export const SSO_PROVIDERS: SsoProvider[] = ['google', 'microsoft', 'okta'];

/** Имя файла для загрузки: исходное имя File или расширение по MIME-типу записи. */
export function audioFilename(file: Blob): string {
  if (typeof File !== 'undefined' && file instanceof File && file.name) return file.name;
  const ext = (file.type || 'audio/wav').split('/')[1]?.split(';')[0] || 'wav';
  return `audio.${ext === 'mpeg' ? 'mp3' : ext}`;
}

export function createEndpoints(client: ApiClient) {
  return {
    auth: {
      register: (data: { email: string; password: string; full_name: string }) =>
        client.post<AuthResult>('/auth/register', data, { auth: false }),
      login: (email: string, password: string) =>
        client.post<AuthResult>('/auth/login', { email, password }, { auth: false }),
      logout: (refreshToken: string | null) =>
        client.post<void>('/auth/logout', refreshToken ? { refresh_token: refreshToken } : {}),
      settings: () => client.get<{ registration_enabled: boolean }>('/auth/settings'),
      ssoProviders: async () => asList<string>(await client.get('/auth/sso/providers')),
      ssoLoginUrl: (provider: SsoProvider) => `${client.baseUrl}/auth/sso/${provider}/login`,
      samlLoginUrl: () => `${client.baseUrl}/auth/saml/login`,
      exchangeCode: (code: string) => client.post<AuthResult>('/auth/exchange', { code }, { auth: false }),
    },
    users: {
      me: () => client.get<User>('/users/me'),
      updateMe: (data: { full_name?: string; preferences?: UserPreferences }) => client.put<User>('/users/me', data),
      changePassword: (current_password: string, new_password: string) =>
        client.post<void>('/users/me/password', { current_password, new_password }),
      list: async (query: { limit?: number; offset?: number; q?: string; role?: Role } = {}) =>
        asList<User>(await client.get('/users', query)),
      setRole: (id: string, role: Role) => client.patch<User>(`/users/${id}/role`, { role }),
      setActive: (id: string, is_active: boolean) => client.patch<User>(`/users/${id}/status`, { is_active }),
      remove: (id: string) => client.delete<void>(`/users/${id}`),
      create: (data: { email: string; full_name: string; password: string; role: Role }) => client.post<User>('/users', data),
    },
    apiKeys: {
      list: async () => asList<ApiKey>(await client.get('/api-keys')),
      create: (data: { name: string; permissions?: string[] | Record<string, unknown>; expires_at?: string | null }) =>
        client.post<ApiKey>('/api-keys', data),
      revoke: (id: string) => client.delete<void>(`/api-keys/${id}`),
    },
    voiceProfiles: {
      list: async () => asList<VoiceProfile>(await client.get('/voice-profiles')),
      create: (data: VoiceProfileInput) => client.post<VoiceProfile>('/voice-profiles', data),
      update: (id: string, data: Partial<VoiceProfileInput>) => client.put<VoiceProfile>(`/voice-profiles/${id}`, data),
      remove: (id: string) => client.delete<void>(`/voice-profiles/${id}`),
      setDefault: (id: string) => client.post<VoiceProfile>(`/voice-profiles/${id}/default`),
    },
    scenarios: {
      list: async (query: { status?: string; tag?: string; search?: string } = {}) =>
        asList<Scenario>(await client.get('/scenarios', query)),
      get: (id: string) => client.get<Scenario>(`/scenarios/${id}`),
      create: (data: Partial<Scenario> & { title: string; content: ScenarioContent }) =>
        client.post<Scenario>('/scenarios', data),
      update: (id: string, data: Partial<Scenario>) => client.put<Scenario>(`/scenarios/${id}`, data),
      remove: (id: string) => client.delete<void>(`/scenarios/${id}`),
      versions: async (id: string) => asList<ScenarioVersion>(await client.get(`/scenarios/${id}/versions`)),
      createVersion: (id: string, data: { content: ScenarioContent; bump?: 'major' | 'minor' | 'patch' }) =>
        client.post<Scenario>(`/scenarios/${id}/versions`, data),
      getVersion: (id: string, version: number) => client.get<ScenarioVersion>(`/scenarios/${id}/versions/${version}`),
      rollback: (id: string, version: number) => client.post<Scenario>(`/scenarios/${id}/rollback`, { version }),
    },
    templates: {
      list: async () => asList<ScenarioTemplate>(await client.get('/templates')),
      create: (data: { name: string; description?: string; content: ScenarioContent }) =>
        client.post<ScenarioTemplate>('/templates', data),
      instantiate: (id: string, title: string) => client.post<Scenario>(`/templates/${id}/scenarios`, { title }),
    },
    sessions: {
      create: (scenario_id: string | null, voice_profile_id: string | null) =>
        client.post<CreatedSession>('/sessions', { scenario_id, voice_profile_id }),
      list: async (query: { limit?: number; offset?: number } = {}) => asList<Session>(await client.get('/sessions', query)),
      get: (id: string) => client.get<Session>(`/sessions/${id}`),
      finish: (id: string) => client.post<Session>(`/sessions/${id}/finish`),
    },
    tts: {
      generate: (data: TtsRequest) => client.post<TtsResult>('/tts/generate', data),
      voices: async (language?: string) => asList<TtsVoice>(await client.get('/tts/voices', { language })),
    },
    assess: {
      utterance: (file: Blob, expected_text: string, language: string, filename = audioFilename(file)) => {
        const form = new FormData();
        form.append('file', file, filename);
        if (expected_text) form.append('expected_text', expected_text);
        if (language) form.append('language', language);
        return client.request<AssessResult>('POST', '/assess/utterance', { form });
      },
    },
    analytics: {
      sessions: async (query: { from?: string; to?: string; user_id?: string } = {}) =>
        asList<Session>(await client.get('/analytics/sessions', query)),
      metrics: (query: { from?: string; to?: string; scope?: 'me' | 'organization' } = {}) =>
        client.get<AnalyticsMetrics>('/analytics/metrics', query),
      userProgress: (query: { user_id?: string; days?: number } = {}) =>
        client.get<UserProgress>('/analytics/user-progress', query),
      export: (format: 'csv' | 'json', query: { from?: string; to?: string } = {}) =>
        client.request<Response>('GET', '/analytics/export', { query: { format, ...query }, raw: true }),
    },
    audit: {
      events: async (query: { action?: string; actor_id?: string; limit?: number; offset?: number } = {}) =>
        asList<AuditEvent>(await client.get('/audit/events', query)),
      verify: () => client.get<{ valid: boolean; checked?: number }>('/audit/verify'),
    },
    bff: {
      dashboard: () => client.get<Dashboard>('/bff/dashboard'),
    },
  };
}

export type Endpoints = ReturnType<typeof createEndpoints>;
export const endpoints = createEndpoints(api);

export function websocketUrl(sessionId: string, websocketUrlFromApi: string | undefined, token: string | null): string {
  const base =
    websocketUrlFromApi && /^wss?:\/\//.test(websocketUrlFromApi)
      ? websocketUrlFromApi
      : `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${websocketUrlFromApi || `/ws/sessions/${sessionId}`}`;
  if (!token) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(token)}`;
}
